<template>
  <div :class="`direction-${direction} ign-auth`">
    <SessionLocked
      v-if="isAborted"
      :name="metaInfo?.name"
    />

    <IgnAuth
      v-else
      :steps="steps"
      :direction="direction"
      :is-next-enabled="isNextEnabled"
    >
      <template #welcome="scope">
        <WelcomePage :data="scope.data" />
      </template>

      <template #verification="scope">
        <VerificationPage :data="scope.data" />
      </template>

      <template #authentication="scope">
        <AuthenticationPage :data="scope.data" />
      </template>
    </IgnAuth>
  </div>
</template>

<script lang="ts" setup>
import { useStore } from 'vuex';
import { Channel } from 'channelx';
import { get, set } from 'lodash-es';
import {
  computed,
  defineAsyncComponent,
  DefineComponent,
  getCurrentInstance,
  h,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { logAction } from '@/shared/services/actionLog/ActionLogService';
import { ActionLogAction } from '@/shared/services/actionLog/ActionLogEntry';
import { useI18n } from '@/shared/helpers/i18n';
import { Channels } from '@/app.manager/events-manager/Channels';

import { AccessEvent } from '@/app.manager/events-manager/events/AccessEvent';
import { configs } from '@/shared/configs';
import { ThemeService, termsService, storageService, localeService, authService, httpClient } from '@/shared/services';
import { Severity } from '@/shared/types';
import TermsOfUseModal from '../../components/TermsOfUseModal/TermsOfUseModal.vue';

import { GREETING_IMG_SRC } from './modules/WelcomePage/constants';
import { IMetaInfo } from './shared/store/shared.types';
import { useIgcModal, useIgcStatistics } from '@/shared/helpers/helpers';
import { i18n } from '@/i18n';
import { AllowedLocales } from '@/shared/services/locale/localeService';
import TermsOfUseLoadingException from '@/shared/models/exceptions/TermsOfUseLoadingException';
import { AccessStatusOriginal } from '@/app.manager/types/AccessStatus';

const WelcomePage = defineAsyncComponent(() => import('./modules/WelcomePage/WelcomePage.vue'));
const VerificationPage = defineAsyncComponent(() => import('./modules/VerificationPage/VerificationPage.vue'));
const AuthenticationPage = defineAsyncComponent(() => import('./modules/AuthenticationPage/AuthenticationPage.vue'));
const SessionLocked = defineAsyncComponent(() => import('./modules/SessionLocked/SessionLocked.vue'));
const IgnAuth = defineAsyncComponent(() => import('./IgnAuth.vue'));
const vNode = getCurrentInstance().vnode;
const store = useStore();
const { t } = useI18n();
const { hideModal, showModal } = useIgcModal();

const metaInfo = computed(() => store.state.loginModule?.metaInfo);
const organization = computed(() => store.state.loginModule?.organization);
const locale = computed(() => store.getters['loginModule/locale']);
const direction = computed(() => store.getters.direction);
const cookiePolicyUrl = computed(() => store.getters['loginModule/cookiePolicyUrl']);
const termsOfUse = computed(() => store.getters['loginModule/termsOfUse']);

const isTermsApproved = ref(false);
const isTermsRequired = ref(false);
const eventsChannel = Channel.use(Channels.eventsChannel);
const isNextEnabled = computed(() => (!isTermsRequired.value || (isTermsRequired.value && isTermsApproved.value)));
const isAborted = computed(() => metaInfo.value?.status?.toString() === AccessStatusOriginal.ABORTED.toString());

watch(locale, (lang: AllowedLocales) => {
  i18n.global.locale.value = lang;
});

onBeforeUnmount(() => {
  // init vnode data
  if (vNode) {
    vNode.key = 'login';
    // @ts-ignore
    vNode.$pageLevel = true;
  }
});

const route = useRoute();

storageService.session.set(configs.LOGIN_PATH_KEY, route.fullPath);
useIgcStatistics()?.addPageTimeEvent('login');

// ...mapActions('loginModule/authenticationModule', ['setAuthError', 'login']),
// ...mapActions('registration/config', ['loadOrganizationConfig']),
// ...mapMutations('loginModule', ['setOrganization']),

function emitAccessSessionId() {
  if (metaInfo.value?.accessSessionId) {
    httpClient.access.post(`public/access-events/${metaInfo.value.accessSessionId}`)
      .then(res => res.data)
      .catch(err => {
        console.log(`emitAccessSessionId - Failed : ${err}`);
        throw err;
      });
  }
}

function loggedIn() {
  logAction({ action: ActionLogAction.loggedIn });
  emitAccessSessionId();

  eventsChannel.publish({ eventName: AccessEvent.LOGGED_IN });
}

function showErrorModal() {
  showModal({
    title: 'terms.errorTitle',
    message: 'terms.errorMessage',
    severity: Severity.error,
  });
}

function updateTermsAndContinue(version: string, jwt: string) {
  authService.setAuthToken(jwt);
  localeService.setLang(locale.value);
  termsService.updateTCVersion(version).then(loggedIn);
}

function showTermsModal(version: string, jwt: string) {
  showModal({
    // @ts-ignore
    component: h(TermsOfUseModal as unknown as DefineComponent, {
      termsText: t('terms.checkboxText'),
      termsOfUseUrl: termsOfUse.value?.url,
      eulafileIdentifier: organization.value.eulafileIdentifier,
      cookiePolicy: t('terms.cookiePolicy', { url: cookiePolicyUrl.value }),
      lang: locale.value,
      continueButton: t('terms.continueButton'),
      onOnClose: () => {
        updateTermsAndContinue(version, jwt);
        isTermsApproved.value = true;
        hideModal();
      },
      onShowTermsError: () => {
        console.log('showTermsError');
        hideModal();
        showErrorModal();
      },
    }),
    title: t('terms.title') as string,
    message: t('terms.textInfo') as string,
    showCloseButton: false,
  }, {
    maxWidth: 340,
    clickToClose: false,
  });
}

function continueSubmit(jwt: string) {
  authService.setAuthToken(jwt);
  localeService.setLang(locale.value);
  loggedIn();
}

async function submit(jwt: string) {
  if (jwt) {
    try {
      const updatedTermsVersion = await termsService.getUpdatedTermsVersion(organization.value?.eulafileIdentifier, termsOfUse.value?.version);

      if (updatedTermsVersion && termsService.needToSignTermOfUse(jwt, updatedTermsVersion)) {
        isTermsRequired.value = true;
        showTermsModal(updatedTermsVersion, jwt);
      } else {
        continueSubmit(jwt);
      }
    } catch (e) {
      if (e instanceof TermsOfUseLoadingException) {
        showErrorModal();
        throw new TermsOfUseLoadingException(e.message);
      } else {
        throw new Error((e as Error).message);
      }
    }
  }
}

const steps = ref<any[]>([
  {
    name: 'welcome',
    data: {
      name: get(metaInfo.value, 'name', ''),
      imageSrc: GREETING_IMG_SRC,
      organization: organization.value,
    },
  },
  {
    name: 'verification',
    data: {
      name: get(metaInfo.value, 'name', ''),
    },
    methods: {
      submit: () => store.dispatch('loginModule/verificationModule/inviteUser', false),
    },
  },
  {
    name: 'authentication',
    data: {
      name: get(metaInfo.value, 'name', ''),
      hasEmail: get(metaInfo.value, 'hasEmail'),
    },
    methods: {
      submit: () => store.dispatch('loginModule/authenticationModule/login')
        .then((res: any) => submit(res?.data?.jwt))
        .catch(e => {
          if (!(e instanceof TermsOfUseLoadingException)) {
            store.dispatch('loginModule/authenticationModule/setAuthError', t('smsCode.loginError'));
          }
          throw new Error(e.message);
        }),
    },
  },
]);

watch(metaInfo, (val: IMetaInfo) => {
  const name = get(val, 'name', '');
  const hasEmail = get(metaInfo.value, 'hasEmail');

  steps.value = steps.value.map(step => {
    const data = {
      ...step.data,
      name,
      hasEmail,
    };

    return { ...step, data };
  });
}, { deep: true });

watch(organization, (org: any) => {
  set(steps.value, '[0].data.organization', org);

  // now that we have org info, we know what theme to set
  storageService.session.set(configs.COLOR_SCHEMA_KEY, org.colorSchema);
  storageService.session.set(configs.ORGANIZATION_LOGO_KEY, org?.logo?.url);
  storageService.session.setObject(configs.ORGANIZATION, org);
  const themeService = new ThemeService(org.colorSchema);

  themeService.setVersion('v1').setTheme();
});
</script>

<style lang="less" scoped>
.content {
  width:  100%;
  height: 100%;
}

.ign-auth {
  height: 100%;
  margin: 0 auto;
}

.direction-rtl {
  direction: rtl;
}

.direction-ltr {
  direction: ltr;
}
</style>
